import React, { useState, useEffect, useCallback } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { Edit } from 'react-feather';
import { fetchTableData, fetchFormData } from '../../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';

const PendingCallForm = () => {
    const [data, setData] = useState([]);
    const [fullData, setFullData] = useState([]);
    const [search, setSearch] = useState('');
    const [callFormStates, setCallFormStates] = useState({});
    const [draftStateId, setDraftStateId] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const callFormApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callform';
    const callFormStateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callformstate';

    const handleSearch = (event) => {
        event.preventDefault();
        setSearch(event.target.value);
    };

    const extractCustomerName = useCallback((row) => {
        const customer = row.customers?.[0];
        return customer?.clientName || customer?.companyName || '';
    }, []);

    const applyFilter = useCallback((items, term) => {
        const normalized = term.trim().toLocaleLowerCase('tr-TR');
        if (!normalized) return items;
        return items.filter((item) => extractCustomerName(item).toLocaleLowerCase('tr-TR').includes(normalized));
    }, [extractCustomerName]);

    useEffect(() => {
        const fetchCallFormStatesAndSetDraft = async () => {
            try {
                const response = await fetchFormData({
                    apiEndpoint: callFormStateApiUrl,
                    page: 1,
                    pageSize: 100
                });

                let allItems = [];
                if (response && response.data && Array.isArray(response.data.items)) {
                    allItems = response.data.items;
                } else if (response && Array.isArray(response.items)) {
                    allItems = response.items;
                } else if (Array.isArray(response)) {
                    allItems = response;
                }

                if (allItems.length > 0) {
                    const stateMap = {};
                    let foundDraftStateId = null;
                    allItems.forEach(item => {
                        if (item && item.id && item.title) {
                            stateMap[item.id] = item.title;
                            if (item.title === 'Taslak') {
                                foundDraftStateId = item.id;
                            }
                        }
                    });
                    setCallFormStates(stateMap);
                    if (foundDraftStateId) {
                        setDraftStateId(foundDraftStateId);
                    } else {
                        console.warn("Warning: 'Taslak' state not found in call form states.");
                    }
                }
            } catch (error) {
                console.error('Error fetching call form states:', error);
            }
        };

        fetchCallFormStatesAndSetDraft();
    }, [callFormStateApiUrl]);

    useEffect(() => {
        if (draftStateId === null && Object.keys(callFormStates).length > 0) {
            if (!Object.values(callFormStates).includes('Taslak')) {
                console.warn("Warning: 'Taslak' state not found among fetched call form states. Data fetching for 'Taslak' items will not proceed.");
            }
            return;
        }
        
        if (!draftStateId) {
            return;
        }

        setLoading(true);
        let apiEndpoint = callFormApiUrl;
        const queryParams = new URLSearchParams();

        queryParams.append('callformstate', draftStateId);

        const queryString = queryParams.toString();
        if (queryString) {
            apiEndpoint += `?${queryString}`;
        }

        fetchTableData({
            apiEndpoint,
            page: 1,
            pageSize: 1000,
            search: null,
            setDataCallback: (items) => {
                setFullData(items);
                setData(items);
            }
        }).finally(() => setLoading(false));

    }, [callFormApiUrl, draftStateId, callFormStates]);

    useEffect(() => {
        setData(applyFilter(fullData, search));
    }, [search, fullData, applyFilter]);

    const handleEdit = (id) => {
        navigate(`/callform/${id}`);
    };

    const columns = [
        {
            name: 'Çağrı Formu No',
            selector: row => row.callFormNumber || '-',
            sortable: true,
        },
        {
            name: 'Müşteri',
            selector: row => extractCustomerName(row) || '-',
            sortable: true,
            grow: 2
        },
        {
            name: 'Plaka',
            selector: row => row.licensePlates?.[0]?.licensePlateNumber || '-',
            sortable: true
        },
        {
            name: 'Vekaletname No',
            selector: row => row.powerOfAttorneys?.[0]?.poaNumber || '-',
            sortable: true
        },
        {
            name: 'Kaza Dosya No',
            selector: row => row.accidentFiles?.[0]?.fileNumber || '-',
            sortable: true
        },
        {
            name: 'Düzenle',
            cell: row => (
                <div className="btn btn-outline-warning d-flex align-items-center justify-content-center" onClick={() => handleEdit(row.id)} title="Düzenle">
                    <Edit size={18}/>
                </div>
            ),
            right: true,
            button: true,
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Sayfalar' title='Bekleyen Çağrı Formları (Taslak)' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Bekleyen Çağrı Formları (Taslak)</H5>
                                <span>Sadece "Taslak" durumunda olan çağrı formları aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content mb-3">
                                    <div className='form-inline me-2 mt-3 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input 
                                                type="text" 
                                                placeholder="Müşteri adı ara"
                                                value={search} 
                                                onChange={handleSearch} 
                                                className="border-0 ms-2 shadow-none" 
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        progressPending={loading}
                                        pagination
                                        selectableRows={false}
                                        stickyActionPosition="right"
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={loading ? null : "Taslak çağrı formu bulunamadı"}
                                    />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default PendingCallForm;
